import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail, LOGO_URL } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, CalendarX, MapPin, Clock } from "lucide-react";

export default function Cancel() {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/token/${token}`);
        setAppt(data);
        if (data.status === "cancelled") setCancelled(true);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/bookings/token/${token}/cancel`);
      setCancelled(true);
      toast.success("Je afspraak is geannuleerd.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-blush flex flex-col items-center justify-center px-5 py-16" data-testid="cancel-page">
      <Link to="/" className="mb-8 flex items-center gap-3">
        <img src={LOGO_URL} alt="Nails Renss" className="h-14 w-14 rounded-full" />
        <span className="font-display text-2xl font-semibold text-brand-charcoal">Nails Renss</span>
      </Link>

      <div className="w-full max-w-md bg-white rounded-3xl border border-brand-border shadow-sm p-8">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-pink" size={32} /></div>
        ) : error ? (
          <div className="text-center">
            <XCircle className="mx-auto text-brand-muted mb-4" size={48} />
            <p className="text-brand-charcoal font-medium">{error}</p>
          </div>
        ) : cancelled ? (
          <div className="text-center" data-testid="cancel-success">
            <CheckCircle2 className="mx-auto text-brand-pink mb-4" size={52} />
            <h1 className="font-display text-3xl font-semibold text-brand-charcoal mb-2">Afspraak geannuleerd</h1>
            <p className="text-brand-muted text-sm">Je ontvangt een bevestiging van de annulatie via e-mail. Tot snel!</p>
            <Link to="/boeken" className="inline-block mt-6 bg-brand-charcoal text-white font-semibold px-6 py-3 rounded-full">Nieuwe afspraak boeken</Link>
          </div>
        ) : (
          <div>
            <h1 className="font-display text-3xl font-semibold text-brand-charcoal mb-1">Je afspraak</h1>
            <p className="text-brand-muted text-sm mb-6">Wil je deze afspraak annuleren?</p>
            <div className="space-y-3 bg-brand-neutral rounded-2xl p-5 mb-6">
              <Row label="Behandeling" value={`${appt.service_category} · ${appt.service_name}`} />
              <Row label="Datum" value={appt.date_display} icon={<Clock size={15} />} />
              <Row label="Tijd" value={appt.start_time} />
              <Row label="Prijs" value={appt.price_display} />
            </div>
            <div className="flex items-start gap-2 text-xs text-brand-muted mb-6">
              <MapPin size={14} className="mt-0.5 text-brand-pink shrink-0" />
              Sint-Amelbergastraat 44, 9700 Mater
            </div>
            {appt.can_cancel ? (
              <button onClick={doCancel} disabled={cancelling} className="w-full bg-brand-charcoal text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" data-testid="cancel-confirm-button">
                {cancelling ? <Loader2 className="animate-spin" size={18} /> : <CalendarX size={18} />} Afspraak annuleren
              </button>
            ) : (
              <div className="text-center bg-brand-blush rounded-2xl p-4 text-sm text-brand-charcoal/80">
                Een afspraak kan niet meer op dezelfde dag geannuleerd worden. Neem indien nodig telefonisch contact op via 0465 00 03 31.
              </div>
            )}
            <Link to="/" className="block text-center mt-4 text-sm text-brand-muted hover:text-brand-charcoal">Terug naar website</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-brand-muted">{label}</span>
      <span className="font-semibold text-brand-charcoal text-right">{value}</span>
    </div>
  );
}
